import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Users, Calendar, MapPin, User, Printer } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const DAY_LABELS = {
  monday: "Lundi", tuesday: "Mardi", wednesday: "Mercredi",
  thursday: "Jeudi", friday: "Vendredi", saturday: "Samedi", sunday: "Dimanche",
};
const DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const EnrollmentsBySlotAdmin = () => {
  const { user } = useAuth();
  const restricted = !!user?.restricted;
  const teacherId = user?.teacherId;
  const [slots, setSlots] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/admin/schedule_slots"),
      api.get("/admin/enrollments"),
    ])
      .then(([s, e]) => {
        const all = s.data || [];
        setSlots(restricted && teacherId ? all.filter((x) => x.teacherId === teacherId) : all);
        setEnrollments(e.data || []);
      })
      .catch(() => toast.error("Erreur au chargement"))
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const byDay = {};
    for (const s of slots) {
      const students = enrollments
        .filter((e) => e.slotId === s.id)
        .map((e) => ({
          id: e.studentId,
          firstName: e.studentFirstName || "",
          lastName: e.studentLastName || "",
          age: e.studentAge,
          familyName: e.familyName || e.parentName || "",
          familyEmail: e.familyEmail || e.email || "",
          familyPhone: e.familyPhone || e.phone || "",
          paymentStatus: e.paymentStatus,
          enrollmentId: e.id,
        }));
      const day = s.day || "unassigned";
      if (!byDay[day]) byDay[day] = [];
      byDay[day].push({ slot: s, students });
    }
    for (const d of Object.keys(byDay)) {
      byDay[d].sort((a, b) => (a.slot.startTime || "").localeCompare(b.slot.startTime || ""));
    }
    return byDay;
  }, [slots, enrollments]);

  const filtered = useMemo(() => {
    if (!q.trim()) return grouped;
    const needle = q.toLowerCase();
    const out = {};
    for (const d of Object.keys(grouped)) {
      const matched = grouped[d].filter(({ slot, students }) => {
        if ((slot.title || "").toLowerCase().includes(needle)) return true;
        if (students.some((s) => `${s.firstName} ${s.lastName} ${s.familyName}`.toLowerCase().includes(needle))) return true;
        return false;
      });
      if (matched.length) out[d] = matched;
    }
    return out;
  }, [grouped, q]);

  const totalStudents = enrollments.length;
  const totalSlotsWithStudents = Object.values(grouped).reduce((n, arr) => n + arr.filter((s) => s.students.length > 0).length, 0);

  return (
    <div>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-serif-display text-2xl sm:text-3xl text-stone-800">Inscriptions par créneau</h1>
          <p className="text-stone-500 mt-1 text-sm sm:text-base">Vue par créneau du planning avec la liste des élèves inscrits.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un créneau, un élève, une famille…"
            data-testid="enrollments-by-slot-search"
            className="bg-white border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blush min-w-[260px]"
          />
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 text-xs bg-stone-100 hover:bg-stone-200 text-stone-700 px-3 py-2 rounded-full"
          >
            <Printer size={13}/> Imprimer
          </button>
        </div>
      </div>

      {loading ? (
        <div className="mt-6 text-stone-500 p-8 text-center">Chargement...</div>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap gap-2 text-xs">
            <span className="bg-blush-light text-deep-rose px-3 py-1.5 rounded-full">{totalStudents} inscription{totalStudents > 1 ? "s" : ""}</span>
            <span className="bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-full">{totalSlotsWithStudents} créneau{totalSlotsWithStudents > 1 ? "x" : ""} occupé{totalSlotsWithStudents > 1 ? "s" : ""}</span>
          </div>

          <div className="mt-6 space-y-8">
            {DAY_ORDER.filter((d) => filtered[d]?.length).map((day) => (
              <div key={day}>
                <h2 className="font-serif-display text-xl text-deep-rose mb-3 flex items-center gap-2">
                  <Calendar size={18}/> {DAY_LABELS[day]}
                </h2>
                <div className="space-y-3">
                  {filtered[day].map(({ slot, students }) => (
                    <div key={slot.id} className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid={`slot-block-${slot.id}`}>
                      <div className="p-4 bg-gradient-to-r from-blush-light/70 to-transparent border-b border-blush-light flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <div className="text-xs uppercase tracking-widest text-stone-500 font-serif-display">
                            {slot.startTime} - {slot.endTime}
                          </div>
                          <div className="font-serif-display text-lg text-stone-800 mt-0.5">{slot.title || "(sans titre)"}</div>
                          <div className="text-stone-500 text-xs mt-0.5 flex items-center gap-2 flex-wrap">
                            {slot.category && <span>{slot.category}</span>}
                            {slot.teacherName && <span>· {slot.teacherName}</span>}
                            {slot.room && <span className="inline-flex items-center gap-1"><MapPin size={11}/> {slot.room}</span>}
                            {slot.price > 0 && <span className="text-emerald-700">· {Number(slot.price).toFixed(0)} €/an</span>}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className={`inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full ${
                            slot.maxStudents > 0 && students.length >= slot.maxStudents
                              ? "bg-red-50 text-red-700"
                              : students.length === 0
                              ? "bg-stone-100 text-stone-500"
                              : "bg-emerald-50 text-emerald-700"
                          }`}>
                            <Users size={12}/> {students.length}{slot.maxStudents > 0 ? ` / ${slot.maxStudents}` : ""}
                          </div>
                        </div>
                      </div>
                      {students.length === 0 ? (
                        <div className="p-4 text-stone-400 text-sm italic">Aucun élève inscrit.</div>
                      ) : (
                        <ul className="divide-y divide-stone-100">
                          {students.map((s) => (
                            <li key={s.enrollmentId} className="p-3 sm:p-4 flex items-center gap-3 flex-wrap" data-testid={`student-row-${s.enrollmentId}`}>
                              <div className="w-8 h-8 rounded-full bg-blush-light text-deep-rose flex items-center justify-center shrink-0">
                                <User size={14}/>
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="font-medium text-stone-800">{s.firstName} {s.lastName}{s.age ? <span className="text-stone-400 text-sm ml-1">({s.age} ans)</span> : null}</div>
                                <div className="text-stone-500 text-xs mt-0.5">
                                  {s.familyName && <span>{s.familyName}</span>}
                                  {s.familyEmail && <span> · <a href={`mailto:${s.familyEmail}`} className="hover:text-deep-rose">{s.familyEmail}</a></span>}
                                  {s.familyPhone && <span> · {s.familyPhone}</span>}
                                </div>
                              </div>
                              <span className={`text-xs px-2 py-1 rounded-full shrink-0 ${
                                s.paymentStatus === "paid" ? "bg-emerald-50 text-emerald-700" :
                                s.paymentStatus === "partial" ? "bg-amber-50 text-amber-700" :
                                "bg-stone-100 text-stone-500"
                              }`}>{s.paymentStatus === "paid" ? "Payé" : s.paymentStatus === "partial" ? "Partiel" : "En attente"}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {Object.keys(filtered).length === 0 && (
              <div className="text-stone-500 text-sm text-center py-12">Aucun résultat.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default EnrollmentsBySlotAdmin;
